import { DeviceLabels } from 'amazon-chime-sdk-component-library-react';
import { Device } from 'amazon-chime-sdk-js';

export class DeviceManager {
  private audioInputDevices: MediaDeviceInfo[] = [];
  private videoInputDevices: MediaDeviceInfo[] = [];
  private audioOutputDevices: MediaDeviceInfo[] = [];
  private selectedAudioInputDevice: string | null = null;
  private selectedVideoInputDevice: string | null = null;
  private selectedAudioOutputDevice: string | null = null;

  async listAndSelectDevices(deviceLabels: DeviceLabels): Promise<void> {
    try {
      // Request permissions if needed - but don't fail if permissions are denied
      try {
        if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Audio) {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Video) {
          await navigator.mediaDevices.getUserMedia({ video: true });
        }
      } catch (permissionError) {
        console.warn('Device permissions denied or no devices available:', permissionError);
        // Continue without devices rather than throwing an error
      }

      // Enumerate all devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // Debug: Log all raw devices before filtering
      console.log('Raw device enumeration results:', devices.map(d => ({ 
        kind: d.kind, 
        label: d.label, 
        deviceId: d.deviceId,
        groupId: d.groupId 
      })));
      
      // Filter devices by type and deduplicate
      const rawAudioInputs = devices.filter(device => device.kind === 'audioinput');
      const rawVideoInputs = devices.filter(device => device.kind === 'videoinput');
      const rawAudioOutputs = devices.filter(device => device.kind === 'audiooutput');
      
      this.audioInputDevices = this.deduplicateDevices(rawAudioInputs);
      this.videoInputDevices = this.deduplicateDevices(rawVideoInputs);
      this.audioOutputDevices = this.deduplicateDevices(rawAudioOutputs);
      
      // Debug logging
      console.log('Device enumeration results:', {
        rawAudioInputs: rawAudioInputs.map(d => ({ id: d.deviceId, label: d.label })),
        rawAudioOutputs: rawAudioOutputs.map(d => ({ id: d.deviceId, label: d.label })),
        deduplicatedAudioInputs: this.audioInputDevices.map(d => ({ id: d.deviceId, label: d.label })),
        deduplicatedAudioOutputs: this.audioOutputDevices.map(d => ({ id: d.deviceId, label: d.label }))
      });
      
      // Special logging for audio output devices to help debug AirPods issues
      const displayPortDevices = this.audioOutputDevices.filter(d => this.isDisplayPortDevice(d));
      console.log('Audio output device analysis:', {
        totalRawAudioOutputs: rawAudioOutputs.length,
        totalDeduplicatedAudioOutputs: this.audioOutputDevices.length,
        devicesWithEmptyLabels: rawAudioOutputs.filter(d => !d.label || d.label.trim() === '').length,
        devicesWithLabels: rawAudioOutputs.filter(d => d.label && d.label.trim() !== '').length,
        displayPortDevices: displayPortDevices.length,
        displayPortLabels: displayPortDevices.map(d => d.label),
        allAudioOutputLabels: rawAudioOutputs.map(d => d.label || '[EMPTY LABEL]')
      });

      // Only select default devices if no device is currently selected
      // This preserves user selections when refreshing the device list
      if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Audio) {
        if (this.audioInputDevices.length > 0 && !this.selectedAudioInputDevice) {
          this.selectedAudioInputDevice = this.audioInputDevices[0].deviceId;
          console.log('Auto-selected first audio input device:', this.selectedAudioInputDevice);
        } else if (this.selectedAudioInputDevice) {
          console.log('Preserving existing audio input device selection:', this.selectedAudioInputDevice);
        }
      }
      if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Video) {
        if (this.videoInputDevices.length > 0 && !this.selectedVideoInputDevice) {
          this.selectedVideoInputDevice = this.videoInputDevices[0].deviceId;
          console.log('Auto-selected first video input device:', this.selectedVideoInputDevice);
        } else if (this.selectedVideoInputDevice) {
          console.log('Preserving existing video input device selection:', this.selectedVideoInputDevice);
        }
      }
      
      // Auto-select audio output device if available and none selected
      if (this.audioOutputDevices.length > 0 && !this.selectedAudioOutputDevice) {
        // Filter out DisplayPort devices (case-insensitive)
        const nonDisplayPortDevices = this.audioOutputDevices.filter(d => 
          !this.isDisplayPortDevice(d)
        );
        
        if (nonDisplayPortDevices.length > 0) {
          // Try to prefer devices with meaningful labels, but fall back to any non-DisplayPort device
          const preferredDevice = nonDisplayPortDevices.find(d => 
            d.label && 
            d.label.trim() !== '' && 
            !d.label.includes('Unknown') &&
            !d.label.includes('Default')
          ) || nonDisplayPortDevices[0];
          
          this.selectedAudioOutputDevice = preferredDevice.deviceId;
          console.log('Auto-selected audio output device:', {
            deviceId: this.selectedAudioOutputDevice,
            label: preferredDevice.label || '[EMPTY LABEL]',
            isPreferred: preferredDevice === nonDisplayPortDevices.find(d => 
              d.label && 
              d.label.trim() !== '' && 
              !d.label.includes('Unknown') &&
              !d.label.includes('Default')
            ),
            excludedDisplayPort: this.audioOutputDevices.length - nonDisplayPortDevices.length
          });
        } else {
          console.log('No non-DisplayPort audio output devices available for auto-selection');
        }
      } else if (this.selectedAudioOutputDevice) {
        console.log('Preserving existing audio output device selection:', this.selectedAudioOutputDevice);
      }
    } catch (error) {
      console.error('Error listing devices:', error);
      // Don't throw error - allow joining without devices
      console.warn('Continuing without device selection due to error');
    }
  }

  getAudioInputDevices(): MediaDeviceInfo[] {
    return this.audioInputDevices;
  }

  getVideoInputDevices(): MediaDeviceInfo[] {
    return this.videoInputDevices;
  }

  getAudioOutputDevices(): MediaDeviceInfo[] {
    return this.audioOutputDevices;
  }

  getSelectedAudioInputDevice(): string | null {
    return this.selectedAudioInputDevice;
  }

  getSelectedVideoInputDevice(): string | null {
    return this.selectedVideoInputDevice;
  }

  getSelectedAudioOutputDevice(): string | null {
    return this.selectedAudioOutputDevice;
  }

  async selectAudioInputDevice(deviceId: string): Promise<void> {
    this.selectedAudioInputDevice = deviceId;
  }

  async selectVideoInputDevice(deviceId: string): Promise<void> {
    this.selectedVideoInputDevice = deviceId;
  }

  async selectAudioOutputDevice(deviceId: string): Promise<void> {
    this.selectedAudioOutputDevice = deviceId;
  }

  /**
   * Check if a device is a DisplayPort device (case-insensitive)
   */
  private isDisplayPortDevice(device: MediaDeviceInfo): boolean {
    return device.label && device.label.toLowerCase().includes('displayport');
  }

  /**
   * Force refresh devices - useful when AirPods are connected after initial load
   */
  async forceRefreshDevices(): Promise<void> {
    console.log('Force refreshing devices...');
    try {
      // Try to get a media stream to trigger device refresh
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      
      // Wait a moment for devices to be released
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Re-enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const rawAudioOutputs = devices.filter(device => device.kind === 'audiooutput');
      
      console.log('Force refresh - found audio output devices:', rawAudioOutputs.map(d => ({
        id: d.deviceId,
        label: d.label || '[EMPTY LABEL]'
      })));
      
      // Update audio output devices
      this.audioOutputDevices = this.deduplicateDevices(rawAudioOutputs);
      
      // If we now have devices and none selected, select the first non-DisplayPort one
      if (this.audioOutputDevices.length > 0 && !this.selectedAudioOutputDevice) {
        const nonDisplayPortDevices = this.audioOutputDevices.filter(d => 
          !this.isDisplayPortDevice(d)
        );
        
        if (nonDisplayPortDevices.length > 0) {
          this.selectedAudioOutputDevice = nonDisplayPortDevices[0].deviceId;
          console.log('Auto-selected audio output device after force refresh:', {
            deviceId: this.selectedAudioOutputDevice,
            label: nonDisplayPortDevices[0].label || '[EMPTY LABEL]',
            excludedDisplayPort: this.audioOutputDevices.length - nonDisplayPortDevices.length
          });
        } else {
          console.log('No non-DisplayPort audio output devices available after force refresh');
        }
      }
    } catch (error) {
      console.warn('Force refresh failed:', error);
    }
  }

  /**
   * Deduplicate devices by removing entries that are essentially the same device
   * but with different labels (e.g., "AirPods" vs "Default - AirPods")
   */
  private deduplicateDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    const deviceMap = new Map<string, MediaDeviceInfo>();
    
    for (const device of devices) {
      // For audio output devices, don't skip devices with empty labels
      // as they might be AirPods or other Bluetooth devices that don't show labels without permissions
      if (device.kind !== 'audiooutput' && (!device.label || device.label.trim() === '')) {
        console.log(`Skipping ${device.kind} device with empty label:`, device.deviceId);
        continue;
      }
      
      // For audio output devices with empty labels, create a fallback label
      const deviceLabel = device.label && device.label.trim() !== '' 
        ? device.label 
        : `Unknown ${device.kind} Device`;
      
      // Normalize the label by removing "Default - " prefix and other common prefixes
      const normalizedLabel = deviceLabel
        .replace(/^Default - /, '')
        .replace(/^Built-in /, '')
        .replace(/^Internal /, '')
        .trim();
      
      // Create a key based on the normalized label (not device ID, since same device can have different IDs)
      const key = `${device.kind}-${normalizedLabel}`;
      
      // Check if we already have this device
      const existingDevice = deviceMap.get(key);
      
      if (!existingDevice) {
        // First time seeing this device
        deviceMap.set(key, device);
        console.log(`Added device: ${deviceLabel} (ID: ${device.deviceId}) with key: ${key}`);
      } else {
        // We already have this device, decide which one to keep
        const currentIsDefault = deviceLabel.startsWith('Default - ');
        const existingIsDefault = existingDevice.label.startsWith('Default - ');
        
        if (currentIsDefault && !existingIsDefault) {
          // Keep the existing non-default version
          console.log(`Skipped duplicate device: ${deviceLabel} (keeping: ${existingDevice.label})`);
        } else if (!currentIsDefault && existingIsDefault) {
          // Replace with the non-default version
          deviceMap.set(key, device);
          console.log(`Replaced device: ${existingDevice.label} with ${deviceLabel}`);
        } else {
          // Both are default or both are non-default, keep the first one
          console.log(`Skipped duplicate device: ${deviceLabel} (keeping: ${existingDevice.label})`);
        }
      }
    }
    
    // If we still have duplicates after the first pass, try a more aggressive approach
    const result = this.aggressiveDeduplication(Array.from(deviceMap.values()));
    
    console.log('Final deduplication result:', result.map(d => ({ label: d.label, id: d.deviceId })));
    return result;
  }

  /**
   * More aggressive deduplication for cases where devices still appear duplicated
   */
  private aggressiveDeduplication(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    const finalDevices: MediaDeviceInfo[] = [];
    const seenLabels = new Set<string>();
    
    for (const device of devices) {
      // Create a very normalized version of the label for comparison
      const deviceLabel = device.label && device.label.trim() !== '' 
        ? device.label 
        : `Unknown ${device.kind} Device`;
        
      const veryNormalizedLabel = deviceLabel
        .toLowerCase()
        .replace(/^default - /, '')
        .replace(/^built-in /, '')
        .replace(/^internal /, '')
        .replace(/\(.*\)/g, '') // Remove anything in parentheses
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
      
      if (!seenLabels.has(veryNormalizedLabel)) {
        seenLabels.add(veryNormalizedLabel);
        finalDevices.push(device);
        console.log(`Final device added: ${deviceLabel} (normalized: ${veryNormalizedLabel})`);
      } else {
        console.log(`Final duplicate removed: ${deviceLabel} (normalized: ${veryNormalizedLabel})`);
      }
    }
    
    return finalDevices.sort((a, b) => {
      // Sort devices with "Default - " prefix to the end
      const aIsDefault = a.label.startsWith('Default - ');
      const bIsDefault = b.label.startsWith('Default - ');
      
      if (aIsDefault && !bIsDefault) return 1;
      if (!aIsDefault && bIsDefault) return -1;
      
      // Otherwise sort alphabetically
      return a.label.localeCompare(b.label);
    });
  }
} 